import React, { useState, useEffect, useCallback } from 'react';
import './AccessibleTools.css';

const FontSizeIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" width="15" height="15">
        <path d="M4 7V4h16v3" /><path d="M9 20h6" /><path d="M12 4v16" />
    </svg>
);

const ContrastIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" width="15" height="15">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a10 10 0 010 20V2z" fill="currentColor" />
    </svg>
);

const AccessibilityIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" width="18" height="18">
        <circle cx="12" cy="4" r="2" />
        <path d="M10 22l2-8 2 8" />
        <path d="M6 12h12" />
        <path d="M8 9s2-2 4-2 4 2 4 2" />
    </svg>
);

interface StorageKeys {
    fontSize: string;
    highContrast: string;
}

const KEYS: StorageKeys = {
    fontSize: 'a11y-font-size',
    highContrast: 'a11y-high-contrast',
};

const FONT_STEP = 1;
const FONT_MIN = 13;
const FONT_MAX = 19;
const FONT_DEFAULT = 15;

type FontSizeKey = 'small' | 'default' | 'large';

const FONT_PRESETS: Record<FontSizeKey, number> = {
    small: 13,
    default: 15,
    large: 18,
};

const getStoredFontSize = (): number => {
    try {
        const stored = localStorage.getItem(KEYS.fontSize);
        if (stored) {
            const val = parseInt(stored, 10);
            if (!isNaN(val) && val >= FONT_MIN && val <= FONT_MAX) return val;
        }
    } catch {}
    return FONT_DEFAULT;
};

const getStoredHighContrast = (): boolean => {
    try {
        return localStorage.getItem(KEYS.highContrast) === 'true';
    } catch {
        return false;
    }
};

const AccessibleTools: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [fontSize, setFontSize] = useState(getStoredFontSize);
    const [highContrast, setHighContrast] = useState(getStoredHighContrast);

    useEffect(() => {
        document.documentElement.style.fontSize = `${fontSize}px`;
        localStorage.setItem(KEYS.fontSize, String(fontSize));
    }, [fontSize]);

    useEffect(() => {
        document.documentElement.setAttribute('data-high-contrast', String(highContrast));
        localStorage.setItem(KEYS.highContrast, String(highContrast));
    }, [highContrast]);

    const increaseFont = useCallback(() => {
        setFontSize(s => Math.min(s + FONT_STEP, FONT_MAX));
    }, []);

    const decreaseFont = useCallback(() => {
        setFontSize(s => Math.max(s - FONT_STEP, FONT_MIN));
    }, []);

    const resetFont = useCallback(() => {
        setFontSize(FONT_DEFAULT);
    }, []);

    const toggleContrast = useCallback(() => {
        setHighContrast(c => !c);
    }, []);

    const atMin = fontSize <= FONT_MIN;
    const atMax = fontSize >= FONT_MAX;
    const isDefault = fontSize === FONT_DEFAULT;
    const activeLabel = highContrast ? 'Disable high contrast' : 'Enable high contrast';

    return (
        <div className="a11y-tools" role="region" aria-label="Accessibility tools">
            <button
                className="a11y-toggle-btn"
                onClick={() => setIsOpen(o => !o)}
                aria-expanded={isOpen}
                aria-label={isOpen ? 'Close accessibility tools' : 'Open accessibility tools'}
                title="Accessibility tools"
            >
                <AccessibilityIcon />
            </button>

            {isOpen && (
                <div className="a11y-panel" role="toolbar" aria-label="Accessibility options">
                    <div className="a11y-group">
                        <span className="a11y-label">Font Size</span>
                        <div className="a11y-font-controls">
                            <button
                                className="a11y-btn"
                                onClick={decreaseFont}
                                disabled={atMin}
                                aria-label="Decrease font size"
                                title="Decrease"
                            >
                                A−
                            </button>
                            <span className="a11y-font-value" aria-live="polite" aria-label={`Current font size ${fontSize} pixels`}>
                                {fontSize}
                            </span>
                            <button
                                className="a11y-btn"
                                onClick={increaseFont}
                                disabled={atMax}
                                aria-label="Increase font size"
                                title="Increase"
                            >
                                A+
                            </button>
                            <button
                                className="a11y-btn a11y-btn-reset"
                                onClick={resetFont}
                                disabled={isDefault}
                                aria-label="Reset font size to default"
                                title="Reset"
                            >
                                <FontSizeIcon />
                            </button>
                        </div>
                    </div>
                    <div className="a11y-group">
                        <span className="a11y-label">Contrast</span>
                        <button
                            className={`a11y-btn a11y-contrast-btn ${highContrast ? 'active' : ''}`}
                            onClick={toggleContrast}
                            aria-label={activeLabel}
                            aria-pressed={highContrast}
                            title={activeLabel}
                        >
                            <ContrastIcon />
                            {highContrast ? 'On' : 'Off'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(AccessibleTools);
